/**
 * CustomerCoins — `/coins` — show the customer's loyalty balance, how
 * coins are earned/redeemed, and a transaction history.
 *
 * iter101 — enhanced history:
 *   • Filter tabs (All / Earned / Redeemed) that segment `tx.delta` sign.
 *   • Order-linked rows: when `tx.reference_id` is set (order id from
 *     an EARN_ORDER / REDEEM_ORDER) the row becomes a Link to
 *     ``/orders/<id>``.
 *   • Running balance shown per row (``tx.balance_after``).
 *   • Coin history is now the DEFAULT feed on this page; wallet-side
 *     history stays at ``/wallet``.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Coins, TrendingUp, TrendingDown, Gift, Info, Wallet, Loader2, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import { BkPageHeader } from "../../components/blinkit/BkUI";

const HISTORY_TABS = [
    { key: "ALL", label: "All" },
    { key: "EARNED", label: "Earned" },
    { key: "REDEEMED", label: "Redeemed" },
];

export default function CustomerCoins() {
    // Blinkit-theme tenants get the shared Blinkit header; the page body is the same for every theme.
    const blinkit = useIsBlinkit();
    const [data, setData] = useState(null);
    const [convertOpen, setConvertOpen] = useState(false);
    const [tab, setTab] = useState("ALL");
    const refresh = () => api.get("/customer/coins").then(({ data }) => setData(data)).catch(() => setData({ balance: 0, transactions: [], config: {} }));
    useEffect(() => { refresh(); }, []);

    const filteredTx = useMemo(() => {
        const rows = data?.transactions || [];
        if (tab === "EARNED") return rows.filter((t) => (t.delta || 0) > 0);
        if (tab === "REDEEMED") return rows.filter((t) => (t.delta || 0) < 0);
        return rows;
    }, [data, tab]);

    if (!data) {
        return <div className="p-6 flex items-center justify-center text-muted-foreground">Loading…</div>;
    }

    const cfg = data.config || {};
    const earnRate = Math.round((cfg.earn_per_rupee || 0) * 100); // % rate
    const c2wEnabled = cfg.coins_to_wallet_enabled !== false;
    const c2wRate = Number(cfg.coins_to_wallet_rate || 0);
    const minConvert = Number(cfg.min_convert_coins || 100);
    const canConvert = c2wEnabled && c2wRate > 0 && data.balance >= minConvert;

    return (
        <div className="pb-32" data-testid="customer-coins">
            {blinkit ? (
                <BkPageHeader title="My coins" back="/profile" />
            ) : (
                <header className="sticky top-0 bg-background z-10 px-4 py-3 border-b border-border flex items-center gap-3">
                    <Link to="/" className="p-2 -ml-2 rounded-lg hover:bg-muted"><ArrowLeft size={20} /></Link>
                    <h1 className="text-lg font-bold">My Coins</h1>
                </header>
            )}

            {/* iter178 — desktop (lg+): two columns, same convention as Cart / Checkout. Below lg both wrappers are plain
                blocks, so phones and tablets render exactly as before. */}
            <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] lg:items-start">
            <div className="min-w-0">
            <div className="mx-4 mt-4 rounded-2xl p-5 bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-md" data-testid="coins-balance-card">
                <p className="text-[11px] uppercase tracking-[0.2em] font-bold opacity-90">Balance</p>
                <div className="flex items-baseline gap-2 mt-1">
                    <Coins size={28} />
                    <span className="text-4xl font-extrabold leading-none">{data.balance}</span>
                    <span className="text-sm font-semibold opacity-90">coins</span>
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs font-semibold">
                    <span className="inline-flex items-center gap-1"><TrendingUp size={12} /> Earned {data.lifetime_earned || 0}</span>
                    <span className="inline-flex items-center gap-1"><TrendingDown size={12} /> Redeemed {data.lifetime_redeemed || 0}</span>
                </div>
                {c2wEnabled && c2wRate > 0 && (
                    <button
                        type="button"
                        data-testid="open-convert-modal"
                        disabled={!canConvert}
                        onClick={() => setConvertOpen(true)}
                        className="mt-4 w-full bg-white/20 hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl py-2.5 inline-flex items-center justify-center gap-2 text-sm transition"
                    >
                        <Wallet size={14} />
                        {canConvert
                            ? `Convert coins → Wallet (₹${c2wRate.toFixed(2)}/coin)`
                            : `Need ${minConvert}+ coins to convert`}
                    </button>
                )}
            </div>

            {cfg.enabled && (
                <section className="mx-4 mt-5 bg-card border border-border rounded-2xl p-4">
                    <h3 className="text-sm font-bold inline-flex items-center gap-2 mb-3"><Info size={14} /> How it works</h3>
                    <ul className="text-sm space-y-1.5 text-muted-foreground">
                        <li><strong className="text-foreground">Earn</strong> {earnRate} coins for every ₹100 you spend.</li>
                        <li><strong className="text-foreground">Redeem</strong> 100 coins = ₹{(cfg.rupee_per_coin || 0) * 100} off your next order.</li>
                        <li>Minimum {cfg.min_redeem_coins || 100} coins to redeem · up to {cfg.max_redeem_pct_of_order || 20}% of order value.</li>
                    </ul>
                </section>
            )}

            </div>
            <div className="min-w-0 lg:[&>*:first-child]:mt-4">
            <section className="mx-4 mt-5" data-testid="coins-history">
                {/* iter101 — filter tabs (All / Earned / Redeemed) */}
                <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Recent activity</h3>
                    <div className="ml-auto inline-flex bg-muted rounded-full p-0.5 gap-0.5">
                        {HISTORY_TABS.map((t) => (
                            <button
                                key={t.key}
                                type="button"
                                data-testid={`coins-tab-${t.key.toLowerCase()}`}
                                onClick={() => setTab(t.key)}
                                className={`text-[11px] font-bold px-3 py-1 rounded-full ${tab === t.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}
                            >{t.label}</button>
                        ))}
                    </div>
                </div>
                {filteredTx.length === 0 ? (
                    <div className="bg-card border border-dashed border-border rounded-2xl p-6 text-center text-muted-foreground text-sm">
                        <Gift size={32} className="mx-auto opacity-40 mb-2" />
                        {tab === "ALL"
                            ? "Place your first order to earn coins."
                            : (tab === "EARNED" ? "No coins earned yet." : "No coins redeemed yet.")}
                    </div>
                ) : (
                    <ul className="bg-card border border-border rounded-2xl divide-y divide-border">
                        {filteredTx.map((t) => {
                            // iter101 — link rows tagged with a reference_id (order id)
                            // to the order-detail page so the customer can trace
                            // where a redemption / earn originated.
                            const orderId = t.reference_id;
                            const linkable = !!orderId && (t.type || "").toUpperCase().includes("ORDER");
                            const body = (
                                <>
                                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${t.delta > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                                        {t.delta > 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold line-clamp-1">{t.note || t.type?.replace(/_/g, " ").toLowerCase()}</p>
                                        <p className="text-[11px] text-muted-foreground">
                                            {new Date(t.created_at).toLocaleString()}
                                            {typeof t.balance_after === "number" && (
                                                <> · Balance <strong className="text-foreground">{t.balance_after}</strong></>
                                            )}
                                        </p>
                                    </div>
                                    <span className={`text-sm font-extrabold ${t.delta > 0 ? "text-emerald-700" : "text-red-700"}`}>
                                        {t.delta > 0 ? "+" : ""}{t.delta}
                                    </span>
                                    {linkable && <ChevronRight size={14} className="text-muted-foreground" />}
                                </>
                            );
                            return linkable ? (
                                <Link
                                    key={t.id}
                                    to={`/orders/${orderId}`}
                                    className="flex items-center gap-3 p-3 hover:bg-muted/40 transition"
                                    data-testid={`coin-tx-${t.id}`}
                                >{body}</Link>
                            ) : (
                                <li key={t.id} className="flex items-center gap-3 p-3" data-testid={`coin-tx-${t.id}`}>
                                    {body}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>
            </div>
            </div>

            {convertOpen && (
                <ConvertCoinsModal
                    balance={data.balance}
                    rate={c2wRate}
                    minConvert={minConvert}
                    onClose={() => setConvertOpen(false)}
                    onConverted={() => { setConvertOpen(false); refresh(); }}
                />
            )}
        </div>
    );
}


/** Convert-coins-to-wallet bottom sheet. The customer types the # of
 * coins (≥ ``minConvert``, ≤ ``balance``) and we POST to
 * ``/customer/coins/convert-to-wallet``. Live preview of the rupee
 * credit at the configured rate. */
function ConvertCoinsModal({ balance, rate, minConvert, onClose, onConverted }) {
    const [coins, setCoins] = useState(minConvert);
    const [busy, setBusy] = useState(false);
    const rupees = (coins * rate).toFixed(2);
    const invalid = coins < minConvert || coins > balance;

    const submit = async () => {
        if (invalid) return;
        setBusy(true);
        try {
            const { data } = await api.post("/customer/coins/convert-to-wallet", { coins });
            toast.success(`Converted ${data.coins_converted} coins → ₹${data.rupees_credited.toFixed(2)} added to wallet`);
            onConverted?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Conversion failed");
        } finally { setBusy(false); }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
            data-testid="convert-coins-modal"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div className="bg-card w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="font-bold text-lg">Convert coins to wallet</h2>
                    <button type="button" onClick={onClose} className="text-muted-foreground" data-testid="convert-close">&times;</button>
                </div>
                <p className="text-xs text-muted-foreground">
                    Convert your earned coins into wallet cash at <strong className="text-foreground">₹{rate.toFixed(2)} per coin</strong>.
                    Min {minConvert} coins. You have <strong className="text-foreground">{balance} coins</strong>.
                </p>
                <div>
                    <label className="text-xs font-bold text-muted-foreground">Coins to convert</label>
                    <input
                        type="number"
                        data-testid="convert-coins-input"
                        value={coins}
                        min={minConvert}
                        max={balance}
                        step={1}
                        onChange={(e) => setCoins(Number(e.target.value || 0))}
                        className="mt-1 w-full px-3 py-2.5 rounded-xl border border-border bg-card text-base font-bold tabular-nums"
                    />
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 rounded-xl px-3 py-2.5 flex items-center justify-between">
                    <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300">You&apos;ll receive in wallet</span>
                    <span data-testid="convert-rupees" className="text-lg font-extrabold text-emerald-700 dark:text-emerald-300">₹{rupees}</span>
                </div>
                <button
                    type="button"
                    data-testid="convert-submit"
                    disabled={busy || invalid}
                    onClick={submit}
                    className="w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <Wallet size={16} />}
                    Convert {coins} coins
                </button>
            </div>
        </div>
    );
}
